import React, { useEffect, useMemo } from 'react'
import { RoleLiteDto } from '../api/role/models/RoleDto'
import personApi from '../api/person/PersonApi'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import PersonUi from './PersonUi'
import Cards from './Cards'
import Card from './Card'
import Button from './Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import LoadingSpinnerOverlay from './LoadingSpinnerOverlay'
import roleApi from '../api/role/RoleApi'
import { toast } from 'sonner'

type Props = {
    role: RoleLiteDto
    hmRemoved: (personId: string) => void
    ephemeralSeedIds?: string[]
}

const DISPLAY_INCREMENT = 8

function NetworkSeeds({ role, hmRemoved, ephemeralSeedIds }: Props): React.JSX.Element {
    const [persons, setPersons] = React.useState<PersonLiteDto[]>([])
    const [isLoading, setIsLoading] = React.useState<boolean>(false)
    const [displayLimit, setDisplayLimit] =
        React.useState<number>(DISPLAY_INCREMENT)

    // Use displayLimit to determine which person IDs to display.
    const displayIds = useMemo(() => {
        const hiringTeamIds: string[] = role.hiringTeamIds || []
        const networkLinkIds: string[] = role.networkLinkIds || []
        const ephemeralIds: string[] = ephemeralSeedIds || []

        const all = [...hiringTeamIds, ...networkLinkIds, ...ephemeralIds]
        return all.slice(0, displayLimit)
    }, [
        role.hiringTeamIds,
        role.networkLinkIds,
        ephemeralSeedIds,
        displayLimit,
    ])

    // Total count of connections.
    const totalCount =
        (role.hiringTeamIds?.length || 0) +
        (role.networkLinkIds?.length || 0) +
        (ephemeralSeedIds?.length || 0)
    // Calculate hidden persons count.
    const hiddenCount = Math.max(totalCount - displayIds.length, 0)
    // Calculate the number of connections that can be shown next.
    const loadCount = Math.min(DISPLAY_INCREMENT, hiddenCount)

    useEffect(() => {
        async function fetchNetworkSeeds() {
            if (displayIds.length === 0) {
                setPersons([])
                return
            }

            // TODO: this should be using the new ManageRoleStore

            try {
                const response = await personApi.getPersonsLite({
                    personIds: displayIds,
                })
                // Sort persons in the order of displayIds.
                const sortedPersons = displayIds
                    .map((id) =>
                        response.persons.find(
                            (p: PersonLiteDto) => p.id === id,
                        ),
                    )
                    .filter(Boolean) as PersonLiteDto[]
                setPersons(sortedPersons)
            } catch (error) {
                console.error('Error fetching persons:', error)
                setPersons([])
            }
        }

        fetchNetworkSeeds()
    }, [displayIds])

    async function handleRemovePerson(personId: string) {
        if (ephemeralSeedIds?.includes(personId)) {
            hmRemoved(personId)
            return
        }

        setIsLoading(true)

        try {
            if (role.hiringTeamIds?.includes(personId)) {
                await roleApi.deleteHiringTeamMember({
                    hiringManagerId: personId,
                    roleId: role.id,
                })
            } else {
                await roleApi.deleteNetworkLinks({
                    personIds: [personId],
                    roleId: role.id,
                })
            }

            hmRemoved(personId)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem removing the network link, please try again.`,
                {
                    duration: 5000,
                },
            )
        } finally {
            setIsLoading(false)
        }
    }

    // Increase the display limit by the preset increment.
    function handleShowMore() {
        setDisplayLimit((prev) => prev + DISPLAY_INCREMENT)
    }

    return (
        <div className="">
            <LoadingSpinnerOverlay show={isLoading}>
                <Cards className="mt-4 bg-gray-50 p-4 rounded-lg flex-wrap">
                    {persons.map((person, index) => (
                        <Card
                            key={`person-${index}`}
                            classes="w-[220px] bg-white"
                            footer={
                                <div className="flex gap-2">
                                    <Button
                                        style="mild-danger"
                                        type="button"
                                        classes="mt-4"
                                        size="small"
                                        onClick={() =>
                                            handleRemovePerson(person.id)
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                    </Button>
                                </div>
                            }
                        >
                            <div className="flex">
                                <PersonUi person={person} />
                                <div className="text-right">
                                    {role.hiringTeamIds?.includes(
                                        person.id,
                                    ) && (
                                        <FontAwesomeIcon
                                            icon={faStar}
                                            className="text-ctxt-blue absolute top-4 right-4"
                                            title="Hiring Manager"
                                        />
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                    {hiddenCount > 0 && (
                        <a
                            className="cursor-pointer text-blue-500 mt-4"
                            onClick={handleShowMore}
                        >
                            Show next {loadCount} network links... (
                            {hiddenCount} hidden)
                        </a>
                    )}
                </Cards>
            </LoadingSpinnerOverlay>
        </div>
    )
}

export default NetworkSeeds
